/***
*
*   PLAN CARD
*   Displays a single plan with its details and actions
*
**********/

import { Card, Badge, Button, Icon } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

export function PlanCard({ plan, onEdit, onDelete, onDuplicate }) {
  const isSystemPlan = plan.id === 'free' || plan.id === 'trial';
  // const isHidden = plan.type === 'hidden';
  const hasPricingStructure = plan.pricing?.base && plan.pricing?.per_user;

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Custom';
    if (price === 0) return 'Free';
    const symbol = plan.currency?.symbol || '$';
    return `${symbol}${price}`;
  };

  const getFeatureCount = () => {
    if (plan.features) {
      return Object.keys(plan.features).length;
    }
    if (plan.modules_enabled) {
      return plan.modules_enabled.length;
    }
    return 0;
  };

  const getPlanBadgeColor = () => {
    if (plan.id === 'free') return 'gray';
    if (plan.id === 'trial') return 'blue';
    if (plan.type === 'hidden') return 'orange';
    if (plan.price === null) return 'purple';
    return 'green';
  };

  const getPlanBadgeText = () => {
    if (plan.id === 'free') return 'FREE';
    if (plan.id === 'trial') return 'TRIAL';
    if (plan.type === 'hidden') return 'HIDDEN';
    if (plan.price === null) return 'CUSTOM';
    return 'PAID';
  };

  const formatInterval = (interval) => {
    if (!interval) return '';
    return interval === 'year' ? '/year' : '/month';
  };

  const getDiscountPercent = () => {
    if (plan.discount_percent) return plan.discount_percent;
    if (plan.metadata?.discount_percent) return parseInt(plan.metadata.discount_percent);
    return null;
  };

  return (
    <Card className={Style.planCard}>
      {/* Badge */}
      <div className={Style.planBadge}>
        <Badge
          text={getPlanBadgeText()}
          color={getPlanBadgeColor()}
        />
      </div>

      {/* Header */}
      <div className={Style.planCardHeader}>
        <div>
          <h3 className={Style.planName}>{plan.name}</h3>

          {/* Pricing Display */}
          {hasPricingStructure ? (
            <div className="mt-2 space-y-1">
              {/* Base Price */}
              <div className="flex items-baseline gap-1">
                <span className="text-xs text-slate-500">Base:</span>
                <span className={Style.planPrice}>
                  {formatPrice(plan.pricing.base.amount)}
                </span>
                {plan.interval && (
                  <span className={Style.planInterval}>{formatInterval(plan.interval)}</span>
                )}
              </div>

              {/* Per User Price */}
              {plan.pricing.per_user.amount > 0 && (
                <div className="flex items-baseline gap-1">
                  <span className="text-xs text-slate-500">Per user:</span>
                  <span className="text-lg font-semibold text-slate-700">
                    {formatPrice(plan.pricing.per_user.amount)}
                  </span>
                  {plan.interval && (
                    <span className="text-xs text-slate-500">{formatInterval(plan.interval)}</span>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Fallback to simple price display
            <div className="flex items-baseline gap-1 mt-2">
              <span className={Style.planPrice}>{formatPrice(plan.price)}</span>
              {plan.price > 0 && plan.interval && (
                <span className={Style.planInterval}>{formatInterval(plan.interval)}</span>
              )}
            </div>
          )}

          {/* Discount */}
          {getDiscountPercent() && (
            <div className="text-sm text-green-600 mt-1">
              {getDiscountPercent()}% discount applied
            </div>
          )}

          {/* Trial Days */}
          {plan.trial_days && (
            <div className="text-sm text-blue-600 mt-1">
              {plan.trial_days} day trial
            </div>
          )}
        </div>
      </div>

      {/* Features Summary */}
      <div className={Style.planFeatures}>
        <div className={Style.planFeature}>
          <Icon image="check" color="green" size={16} />
          <span>{getFeatureCount()} features enabled</span>
        </div>
        {plan.features_summary?.file_storage && (
          <div className={Style.planFeature}>
            <Icon image="hard-drive" color="blue" size={16} />
            <span>{plan.features_summary.file_storage} {plan.features_summary.file_storage_unit || 'MB'} storage</span>
          </div>
        )}
        {plan.features_summary?.manage_people && (
          <div className={Style.planFeature}>
            <Icon image="users" color="purple" size={16} />
            <span>{plan.features_summary.manage_people} employees</span>
          </div>
        )}
        {plan.features_summary?.hiring_recruitment !== undefined && (
          <div className={Style.planFeature}>
            <Icon image="briefcase" color="orange" size={16} />
            <span>
              {plan.features_summary.hiring_recruitment === 'unlimited' || plan.features_summary.hiring_recruitment === null
                ? 'Unlimited'
                : plan.features_summary.hiring_recruitment} job openings
            </span>
          </div>
        )}
        {plan.add_ons_available && plan.add_ons_available.length > 0 && (
          <div className={Style.planFeature}>
            <Icon image="package" color="teal" size={16} />
            <span>{plan.add_ons_available.length} add-ons available</span>
          </div>
        )}
      </div>

      {/* Modules List */}
      {plan.modules_enabled && plan.modules_enabled.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-2">Enabled Modules:</p>
          <div className="flex flex-wrap gap-1">
            {plan.modules_enabled.slice(0, 5).map((module) => (
              <span key={module} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                {module.replace(/_/g, ' ')}
              </span>
            ))}
            {plan.modules_enabled.length > 5 && (
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                +{plan.modules_enabled.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className={Style.planActions}>
        <Button
          text="Edit"
          icon="edit-2"
          size="small"
          color="blue"
          outline
          action={() => onEdit(plan)}
        />
        <Button
          text="Duplicate"
          icon="copy"
          size="small"
          color="gray"
          outline
          disabled={true}
          tooltip="Duplicating plans is currently disabled."
        />
        {!isSystemPlan && (
          <Button
            text="Delete"
            icon="trash-2"
            size="small"
            color="red"
            outline
            disabled={true}
            tooltip="Deleting plans is currently disabled."
          />
        )}
      </div>
    </Card>
  );
}